import React, { useState } from 'react';
import AdminLayout from '../../../components/AdminLayout';
import { Download, FileText, Users, File, Calendar, CheckCircle } from 'lucide-react';
import educentialApi from '../../../api/educentialApi';

const DataExport = () => {
  const [exportConfig, setExportConfig] = useState({
    resource: 'users',
    format: 'csv',
    dateFrom: '',
    dateTo: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const resources = [
    { value: 'users', label: 'Users', icon: Users, description: 'Export all user data including profiles and roles' },
    { value: 'applications', label: 'Applications', icon: FileText, description: 'Export application data with status and details' },
    { value: 'documents', label: 'Documents', icon: File, description: 'Export document metadata and review status' },
    { value: 'all', label: 'Complete Data', icon: Download, description: 'Export all platform data (users, applications, documents)' }
  ];

  const formats = [
    { value: 'csv', label: 'CSV', description: 'Comma-separated values (Excel compatible)' },
    { value: 'json', label: 'JSON', description: 'JavaScript Object Notation' },
    { value: 'excel', label: 'Excel', description: 'Microsoft Excel format (.xlsx)' }
  ];

  const handleExport = async () => {
    setLoading(true);
    setSuccess(false);

    try {
      const response = await educentialApi.post('/api/admin/reports/export', {
        ...exportConfig,
        dateRange: {
          dateFrom: exportConfig.dateFrom,
          dateTo: exportConfig.dateTo
        }
      }, {
        responseType: exportConfig.format === 'csv' ? 'text' : 'json'
      });

      // Create blob and download
      let blob;
      let fileName;

      if (exportConfig.format === 'csv') {
        blob = new Blob([response.data], { type: 'text/csv' });
        fileName = `${exportConfig.resource}-export-${new Date().toISOString().split('T')[0]}.csv`;
      } else if (exportConfig.format === 'json') {
        blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
        fileName = `${exportConfig.resource}-export-${new Date().toISOString().split('T')[0]}.json`;
      } else {
        // Excel format (for now, download as JSON - would need proper Excel library)
        blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
        fileName = `${exportConfig.resource}-export-${new Date().toISOString().split('T')[0]}.json`;
      }

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (error) {
      console.error('Error exporting data:', error);
      alert('Failed to export data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AdminLayout title="Data Export">
      <div className="p-6">
        <div className="max-w-4xl mx-auto space-y-6">
          {/* Header */}
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Export Data</h2>
            <p className="text-gray-600 mt-1">Download platform data in various formats for analysis or backup</p>
          </div>

          {/* Success Message */}
          {success && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center space-x-3">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <div>
                <p className="text-green-900 font-medium">Export successful!</p>
                <p className="text-green-700 text-sm">Your file has been downloaded.</p>
              </div>
            </div>
          )}

          {/* Select Resource */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">1. Select Data to Export</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {resources.map((resource) => {
                const Icon = resource.icon;
                const isSelected = exportConfig.resource === resource.value;

                return (
                  <button
                    key={resource.value}
                    onClick={() => setExportConfig({ ...exportConfig, resource: resource.value })}
                    className={`p-4 rounded-lg border-2 transition-all text-left ${
                      isSelected
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-start space-x-3">
                      <div className={`p-2 rounded-lg ${isSelected ? 'bg-blue-100' : 'bg-gray-100'}`}>
                        <Icon className={`w-5 h-5 ${isSelected ? 'text-blue-600' : 'text-gray-600'}`} />
                      </div>
                      <div className="flex-1">
                        <p className={`font-medium ${isSelected ? 'text-blue-900' : 'text-gray-900'}`}>
                          {resource.label}
                        </p>
                        <p className={`text-sm mt-1 ${isSelected ? 'text-blue-700' : 'text-gray-600'}`}>
                          {resource.description}
                        </p>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Select Format */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">2. Choose Export Format</h3>
            <div className="space-y-3">
              {formats.map((format) => {
                const isSelected = exportConfig.format === format.value;

                return (
                  <label
                    key={format.value}
                    className={`flex items-center space-x-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                      isSelected
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name="format"
                      value={format.value}
                      checked={isSelected}
                      onChange={(e) => setExportConfig({ ...exportConfig, format: e.target.value })}
                      className="text-blue-600"
                    />
                    <div className="flex-1">
                      <p className={`font-medium ${isSelected ? 'text-blue-900' : 'text-gray-900'}`}>
                        {format.label}
                      </p>
                      <p className={`text-sm ${isSelected ? 'text-blue-700' : 'text-gray-600'}`}>
                        {format.description}
                      </p>
                    </div>
                  </label>
                );
              })}
            </div>
          </div>

          {/* Date Range (Optional) */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">3. Date Range (Optional)</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <div className="flex items-center space-x-2">
                    <Calendar className="w-4 h-4" />
                    <span>From Date</span>
                  </div>
                </label>
                <input
                  type="date"
                  value={exportConfig.dateFrom}
                  onChange={(e) => setExportConfig({ ...exportConfig, dateFrom: e.target.value })}
                  className="w-full border-gray-300 rounded-md"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <div className="flex items-center space-x-2">
                    <Calendar className="w-4 h-4" />
                    <span>To Date</span>
                  </div>
                </label>
                <input
                  type="date"
                  value={exportConfig.dateTo}
                  onChange={(e) => setExportConfig({ ...exportConfig, dateTo: e.target.value })}
                  className="w-full border-gray-300 rounded-md"
                />
              </div>
            </div>
            <p className="text-sm text-gray-600 mt-2">
              Leave empty to export all data without date filtering
            </p>
          </div>

          {/* Export Button */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-gray-900">Ready to export?</p>
                <p className="text-sm text-gray-600 mt-1">
                  Your download will begin immediately after clicking the button
                </p>
              </div>
              <button
                onClick={handleExport}
                disabled={loading}
                className="flex items-center space-x-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Download className="w-5 h-5" />
                <span>{loading ? 'Exporting...' : 'Export Data'}</span>
              </button>
            </div>
          </div>

          {/* Info Box */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
            <h4 className="font-semibold text-blue-900 mb-2">Export Information</h4>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• Exported data includes all fields for the selected resource</li>
              <li>• CSV format is compatible with Excel and Google Sheets</li>
              <li>• JSON format is suitable for technical analysis and data processing</li>
              <li>• Large exports may take a few moments to prepare</li>
              <li>• All exports respect user privacy and data protection policies</li>
            </ul>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default DataExport;
